import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { subscriptionsApi } from "../lib/subscriptionsApi";

/**
 * Module D — Terminal Balcão (PDV touchscreen)
 * 3 columns: comandas list / order detail / catalog touch
 */
const PosTerminal = () => {
    const {
        brand,
        comandas,
        services,
        products,
        professionals,
        clients,
        createComanda,
        addItemToComanda,
        removeItemFromComanda,
        closeComanda,
        upsertClient,
        acknowledgeComanda,
    } = useSalon();

    const [selectedId, setSelectedId] = useState(comandas[0]?.id || null);
    const [catalogTab, setCatalogTab] = useState("services");
    const [paying, setPaying] = useState(null); // "pix" | "card" | null
    const [walkInOpen, setWalkInOpen] = useState(false);
    const [activeSubscription, setActiveSubscription] = useState(null);

    const active = comandas.find((c) => c.id === selectedId) || null;

    // Buscar assinatura ativa do cliente ao mudar de comanda
    useEffect(() => {
        if (!active?.clientId) { setActiveSubscription(null); return; }
        let cancelled = false;
        subscriptionsApi.getByClient(active.clientId)
            .then((sub) => { if (!cancelled) setActiveSubscription(sub || null); })
            .catch(() => { if (!cancelled) setActiveSubscription(null); });
        return () => { cancelled = true; };
    }, [active?.clientId, active?.id]);

    // Verifica se um serviço está coberto pelo plano ativo
    const isCoveredByPlan = (item) => {
        if (!activeSubscription || !activeSubscription.plan) return false;
        if (item.kind !== "service") return false;
        const covered = activeSubscription.plan.service_ids || [];
        return covered.includes(item.serviceId || item.id);
    };

    const totals = useMemo(() => {
        if (!active) return { subtotal: 0, service: 0, total: 0, commission: 0 };
        const subtotal = active.items.reduce((s, i) => s + i.price * i.qty, 0);
        const service = subtotal * 0.05;
        const commission = active.items.reduce(
            (s, i) => s + i.price * i.qty * ((i.commissionRate ?? 0) / 100),
            0
        );
        return { subtotal, service, total: subtotal + service, commission };
    }, [active]);

    const handleNewComanda = () => {
        setWalkInOpen(true);
    };

    const handleWalkInConfirm = ({ clientId, clientName, professionalId }) => {
        const id = createComanda(clientName, professionalId, clientId);
        setSelectedId(id);
        setWalkInOpen(false);
        const pro = professionals.find((p) => p.id === professionalId);
        toast.success("Comanda aberta", {
            description: `${clientName} → ${pro?.name || "—"}`,
        });
    };

    const handleSelectComanda = (id) => {
        setSelectedId(id);
        // Stop the pulsing alert as soon as the cashier engages the comanda
        const c = comandas.find((x) => x.id === id);
        if (c && c.readyToPay) acknowledgeComanda(id);
    };

    const handleAdd = (item, kind) => {
        if (!active) {
            toast.error("Selecione ou crie uma comanda primeiro.");
            return;
        }
        addItemToComanda(active.id, { ...item, kind });
        toast.success(`${item.name} adicionado`, { description: `+R$ ${item.price.toFixed(2)}` });
    };

    const handlePay = (method) => {
        if (!active) {
            toast.error("Nenhuma comanda selecionada.");
            return;
        }
        if (active.items.length === 0) {
            toast.error("Comanda vazia.", { description: "Insira pelo menos um item." });
            return;
        }
        setPaying(method);
        setTimeout(async () => {
            // Se houver assinatura ativa, descontar 1 uso por serviço coberto no plano
            if (activeSubscription && activeSubscription.id) {
                const covered = active.items.filter((it) => isCoveredByPlan(it));
                for (const it of covered) {
                    try {
                        await subscriptionsApi.use(activeSubscription.id, {
                            comanda_id: active.id,
                            service_id: it.serviceId || it.id,
                        });
                    } catch (e) {
                        // Silencioso: se limite atingido, o backend responde 409;
                        // fluxo do PDV continua normalmente (cobrança padrão).
                    }
                }
            }
            closeComanda(active.id);
            toast.success(`Pagamento ${method.toUpperCase()} confirmado`, {
                description: `R$ ${totals.total.toFixed(2)} — comanda fechada.`,
            });
            setSelectedId(comandas.find((c) => c.id !== active.id)?.id || null);
            setPaying(null);
        }, 1600);
    };

    const minsAgo = (ts) => Math.max(1, Math.floor((Date.now() - ts) / 60000));

    return (
        <div data-testid="pos-terminal-view" className="grid lg:grid-cols-[280px_1fr_360px] gap-4 h-[calc(100vh-130px)]">
            {/* Column 1: comandas list */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4 flex flex-col">
                <header className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                            Comandas Ativas
                        </div>
                        <div className="salon-heading text-lg text-white">{comandas.length} clientes</div>
                    </div>
                </header>
                <button
                    data-testid="new-comanda-btn"
                    onClick={handleNewComanda}
                    className="min-h-[48px] rounded-xl border border-dashed border-white/15 hover:border-white/40 text-slate-300 hover:text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
                >
                    <i className="fa-solid fa-plus"></i> Nova Comanda
                </button>
                <div className="mt-3 space-y-2 overflow-y-auto pr-1">
                    {comandas.length === 0 && (
                        <div className="text-xs text-slate-500 text-center py-12">
                            <i className="fa-solid fa-receipt text-2xl mb-2 opacity-50"></i>
                            <p>Nenhuma comanda ativa</p>
                        </div>
                    )}
                    {comandas.map((c) => {
                        const isActive = selectedId === c.id;
                        const subtotal = c.items.reduce((s, i) => s + i.price * i.qty, 0);
                        return (
                            <button
                                key={c.id}
                                data-testid={`comanda-${c.id}`}
                                data-ready-to-pay={c.readyToPay ? "true" : "false"}
                                onClick={() => handleSelectComanda(c.id)}
                                className={`w-full min-h-[68px] text-left rounded-xl p-3 transition-all border ${
                                    c.readyToPay
                                        ? "ready-to-pay-pulse bg-emerald-500/10"
                                        : isActive
                                            ? "bg-slate-950/80 border-white/20 shadow-lg"
                                            : "bg-slate-950/40 border-white/5 hover:border-white/10"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-white">{c.client}</span>
                                    {c.readyToPay ? (
                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-500/25 text-emerald-200 border border-emerald-400/40">
                                            <i className="fa-solid fa-circle-check mr-1 text-[8px]"></i>
                                            Pronto
                                        </span>
                                    ) : (
                                        <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                            {minsAgo(c.createdAt)}m
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 flex items-center justify-between text-xs">
                                    <span className={c.readyToPay ? "text-emerald-200/80" : "text-slate-400"}>
                                        <i className="fa-solid fa-cart-shopping mr-1.5"></i>{c.items.length} itens
                                    </span>
                                    <span
                                        className="font-mono"
                                        style={{ color: c.readyToPay ? "#34d399" : brand.primaryColor }}
                                    >
                                        R$ {subtotal.toFixed(2)}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Column 2: order detail */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5 flex flex-col">
                {!active ? (
                    <EmptyState onCreate={handleNewComanda} />
                ) : (
                    <>
                        <header className="flex items-center justify-between pb-4 border-b border-white/5">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    Comanda em aberto
                                </div>
                                <div className="salon-heading text-2xl text-white">{active.client}</div>
                                <div className="text-xs text-slate-400 mt-0.5">
                                    Profissional: {professionals.find((p) => p.id === active.professional)?.name || "—"}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    Total
                                </div>
                                <div
                                    data-testid="order-total"
                                    className="salon-heading text-3xl"
                                    style={{ color: brand.primaryColor }}
                                >
                                    R$ {totals.total.toFixed(2)}
                                </div>
                            </div>
                        </header>

                        {activeSubscription && (
                            <div data-testid="pos-subscription-banner" className="mt-3 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-amber-400/5 p-3">
                                <div className="flex items-center gap-2">
                                    <i className="fa-solid fa-medal text-amber-300"></i>
                                    <span className="text-sm font-semibold text-white">Cliente do Clube · {activeSubscription.plan?.name}</span>
                                    <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full border ${activeSubscription.status === "expiring" ? "border-amber-500/40 text-amber-200 bg-amber-500/10" : "border-emerald-500/40 text-emerald-200 bg-emerald-500/10"}`}>
                                        {activeSubscription.status === "expiring" ? "Vencendo" : "Ativo"}
                                    </span>
                                </div>
                                <div className="mt-1 text-xs text-slate-300">
                                    {activeSubscription.plan?.uses_per_month === 0
                                        ? <>Usos <span className="text-white font-semibold">ilimitados</span> este mês</>
                                        : <>Usos restantes: <span className="text-white font-semibold">{Math.max(0, (activeSubscription.plan?.uses_per_month || 0) - (activeSubscription.uses_this_month || 0))}</span> de {activeSubscription.plan?.uses_per_month}</>
                                    }
                                    <span className="mx-2 text-slate-600">·</span>
                                    Renovação: <span className="text-slate-200">{activeSubscription.next_renewal}</span>
                                </div>
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto py-4 space-y-2">
                            {active.items.length === 0 && (
                                <div className="text-center py-12 text-slate-500">
                                    <i className="fa-solid fa-hand-pointer text-3xl mb-3 opacity-40"></i>
                                    <p className="text-sm">Toque em um item do catálogo para adicionar</p>
                                </div>
                            )}
                            {active.items.map((it) => (
                                <div
                                    key={it.id}
                                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-950/60 border border-white/5"
                                >
                                    <div
                                        className="h-10 w-10 rounded-lg flex items-center justify-center"
                                        style={{
                                            background: `${brand.primaryColor}22`,
                                            color: brand.primaryColor,
                                        }}
                                    >
                                        <i className={`fa-solid ${it.icon}`}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-white truncate">
                                            {it.name}
                                            {it.qty > 1 && <span className="text-slate-400"> x{it.qty}</span>}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                            <span className="text-[10px] uppercase tracking-wider text-slate-500">
                                                {it.kind === "service" ? "Serviço" : "Produto"}
                                            </span>
                                            {isCoveredByPlan(it) && (
                                                <span data-testid={`item-covered-${it.id}`}
                                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border bg-amber-500/15 text-amber-300 border-amber-500/40">
                                                    <i className="fa-solid fa-medal text-[8px]"></i>INCLUSO NO PLANO
                                                </span>
                                            )}
                                            {it.commissionRate !== undefined && (
                                                <span
                                                    data-testid={`item-commission-${it.id}`}
                                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border ${
                                                        it.commissionSource === "item"
                                                            ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                                                            : "bg-slate-500/15 text-slate-300 border-slate-500/30"
                                                    }`}
                                                    title={
                                                        it.commissionSource === "item"
                                                            ? "Comissão específica do item"
                                                            : "Comissão padrão do colaborador"
                                                    }
                                                >
                                                    <i className="fa-solid fa-percent text-[8px]"></i>
                                                    {it.commissionRate}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="font-mono text-sm text-white">
                                        R$ {(it.price * it.qty).toFixed(2)}
                                    </div>
                                    <button
                                        data-testid={`remove-item-${it.id}`}
                                        onClick={() => removeItemFromComanda(active.id, it.id)}
                                        className="h-9 w-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Summary */}
                        <div className="border-t border-white/5 pt-4 space-y-2 text-sm">
                            <Row label="Subtotal" value={`R$ ${totals.subtotal.toFixed(2)}`} />
                            <Row label="Taxa de serviço (5%)" value={`R$ ${totals.service.toFixed(2)}`} muted />
                            {totals.commission > 0 && (
                                <Row
                                    label={
                                        <span className="inline-flex items-center gap-1.5">
                                            <i className="fa-solid fa-percent text-[10px] text-amber-400"></i>
                                            Comissão prevista (snapshot)
                                        </span>
                                    }
                                    value={`R$ ${totals.commission.toFixed(2)}`}
                                    muted
                                />
                            )}
                            <Row label="TOTAL" value={`R$ ${totals.total.toFixed(2)}`} bold primary={brand.primaryColor} />
                        </div>

                        {/* Payment */}
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <button
                                data-testid="pay-pix-btn"
                                onClick={() => handlePay("pix")}
                                disabled={paying !== null || !active || active.items.length === 0}
                                className="min-h-[68px] rounded-2xl bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-slate-950 font-bold uppercase tracking-wider transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-70 flex items-center justify-center gap-2"
                            >
                                {paying === "pix" ? (
                                    <><i className="fa-solid fa-spinner fa-spin"></i> Processando…</>
                                ) : (
                                    <><i className="fa-brands fa-pix"></i> Pagar PIX</>
                                )}
                            </button>
                            <button
                                data-testid="pay-card-btn"
                                onClick={() => handlePay("card")}
                                disabled={paying !== null || !active || active.items.length === 0}
                                className="min-h-[68px] rounded-2xl text-white font-bold uppercase tracking-wider transition-all shadow-lg disabled:opacity-70 flex items-center justify-center gap-2"
                                style={{
                                    background: brand.primaryColor,
                                    boxShadow: `0 10px 30px -10px ${brand.primaryColor}80`,
                                }}
                            >
                                {paying === "card" ? (
                                    <><i className="fa-solid fa-spinner fa-spin"></i> Processando…</>
                                ) : (
                                    <><i className="fa-solid fa-credit-card"></i> Pagar Cartão</>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Column 3: Catalog */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4 flex flex-col">
                <header className="mb-3">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        Catálogo Touch
                    </div>
                    <div className="salon-heading text-lg text-white">Adicionar Item</div>
                </header>
                <div className="grid grid-cols-2 gap-1 p-1 bg-slate-950/60 rounded-xl mb-3">
                    {[
                        { id: "services", label: "Serviços", icon: "fa-scissors" },
                        { id: "products", label: "Produtos", icon: "fa-jar" },
                    ].map((tab) => {
                        const active = catalogTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                data-testid={`catalog-tab-${tab.id}`}
                                onClick={() => setCatalogTab(tab.id)}
                                className={`min-h-[44px] rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                                    active ? "bg-white text-slate-900" : "text-slate-400 hover:text-white"
                                }`}
                            >
                                <i className={`fa-solid ${tab.icon} mr-1.5`}></i>
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <div className="overflow-y-auto pr-1 grid grid-cols-2 gap-2">
                    {(catalogTab === "services" ? services : products)
                        .filter((it) => it.active !== false)
                        .map((it) => (
                        <button
                            key={it.id}
                            data-testid={`catalog-item-${it.id}`}
                            onClick={() => handleAdd(it, catalogTab === "services" ? "service" : "product")}
                            className="min-h-[100px] rounded-xl p-3 bg-slate-950/60 hover:bg-slate-950/90 border border-white/5 hover:border-white/20 transition-all text-left active:scale-95 relative"
                        >
                            {it.commission !== null && it.commission !== undefined && it.commission !== "" && (
                                <span
                                    className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] font-mono font-bold"
                                    title="Comissão específica deste item"
                                >
                                    <i className="fa-solid fa-percent text-[7px]"></i>
                                    {it.commission}%
                                </span>
                            )}
                            <div
                                className="h-9 w-9 rounded-lg flex items-center justify-center mb-2"
                                style={{
                                    background: `${brand.primaryColor}22`,
                                    color: brand.primaryColor,
                                }}
                            >
                                <i className={`fa-solid ${it.icon}`}></i>
                            </div>
                            <div className="text-xs font-semibold text-white truncate">{it.name}</div>
                            <div className="text-[11px] font-mono mt-0.5" style={{ color: brand.primaryColor }}>
                                R$ {it.price.toFixed(2)}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <WalkInModal
                open={walkInOpen}
                onClose={() => setWalkInOpen(false)}
                onConfirm={handleWalkInConfirm}
                clients={clients}
                professionals={professionals}
                upsertClient={upsertClient}
            />
        </div>
    );
};

const Row = ({ label, value, muted, bold, primary }) => (
    <div className="flex items-center justify-between">
        <span className={`${bold ? "uppercase tracking-[0.2em] text-xs font-bold text-white" : muted ? "text-slate-500" : "text-slate-400"}`}>
            {label}
        </span>
        <span
            className={`font-mono ${bold ? "text-xl font-bold" : ""}`}
            style={bold && primary ? { color: primary } : { color: muted ? "#94a3b8" : "white" }}
        >
            {value}
        </span>
    </div>
);

const EmptyState = ({ onCreate }) => (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
        <div className="h-20 w-20 rounded-2xl bg-slate-950/60 flex items-center justify-center mb-4">
            <i className="fa-solid fa-receipt text-3xl text-slate-600"></i>
        </div>
        <div className="salon-heading text-2xl text-white">Nenhuma comanda selecionada</div>
        <p className="text-sm text-slate-400 mt-2 max-w-sm">
            Selecione uma comanda à esquerda ou crie uma nova para começar a lançar consumo.
        </p>
        <button
            onClick={onCreate}
            data-testid="empty-create-comanda"
            className="mt-5 px-5 min-h-[44px] rounded-xl bg-white text-slate-900 text-sm font-semibold uppercase tracking-wider"
        >
            <i className="fa-solid fa-plus mr-2"></i>Criar Nova Comanda
        </button>
    </div>
);

// ─── Walk-in Modal ────────────────────────────────────────────────────────
const WalkInModal = ({ open, onClose, onConfirm, clients, professionals, upsertClient }) => {
    const [step, setStep] = useState("search"); // "search" | "create" | "pro"
    const [query, setQuery] = useState("");
    const [selectedClient, setSelectedClient] = useState(null);
    const [draft, setDraft] = useState({ name: "", phone: "", email: "" });
    const [selectedProId, setSelectedProId] = useState(null);

    const filteredClients = useMemo(() => {
        const raw = query.trim();
        if (!raw) return clients.slice(0, 8);
        const digitsQuery = raw.replace(/\D/g, "");
        const lowerQuery = raw.toLowerCase();
        return clients.filter((c) => {
            const phoneDigits = (c.phone || "").replace(/\D/g, "");
            const email = (c.email || "").toLowerCase();
            const name = (c.name || "").toLowerCase();
            if (digitsQuery.length >= 2 && phoneDigits.includes(digitsQuery)) return true;
            if (name.includes(lowerQuery)) return true;
            if (email.includes(lowerQuery)) return true;
            return false;
        }).slice(0, 12);
    }, [clients, query]);

    if (!open) return null;

    const handleClose = () => {
        setStep("search");
        setQuery("");
        setSelectedClient(null);
        setDraft({ name: "", phone: "", email: "" });
        setSelectedProId(null);
        onClose();
    };

    const handlePickExisting = (c) => {
        setSelectedClient(c);
        setStep("pro");
    };

    const handleQuickCreate = () => {
        if (!draft.name.trim()) {
            toast.error("Informe ao menos o nome do cliente.");
            return;
        }
        // Persist new client (id will be derived from phone tail or timestamp by upsertClient)
        upsertClient({
            name: draft.name.trim(),
            phone: draft.phone.trim(),
            email: draft.email.trim(),
            document: "",
            birthDate: "",
            address: "",
            notes: "Cliente cadastrado via Walk-in",
        });
        // Reflect locally — clients list will re-render from context
        setSelectedClient({ id: null, name: draft.name.trim(), phone: draft.phone.trim() });
        setStep("pro");
        toast.success("Cliente adicionado", { description: draft.name.trim() });
    };

    const handleConfirmPro = () => {
        if (!selectedProId) {
            toast.error("Selecione um colaborador para iniciar o atendimento.");
            return;
        }
        onConfirm({
            clientId: selectedClient?.id || null,
            clientName: selectedClient?.name || draft.name.trim(),
            professionalId: selectedProId,
        });
        // Reset state for next use
        setStep("search");
        setQuery("");
        setSelectedClient(null);
        setDraft({ name: "", phone: "", email: "" });
        setSelectedProId(null);
    };

    return (
        <div
            data-testid="walkin-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="rounded-2xl border border-white/10 bg-slate-950 max-w-xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
                <header className="flex items-start justify-between pb-3 border-b border-white/5">
                    <div>
                        <h3 className="salon-heading text-xl text-white">Nova Comanda · Walk-in</h3>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                            {step === "search" && "1 · Identifique o cliente"}
                            {step === "create" && "1b · Cadastro rápido"}
                            {step === "pro" && "2 · Quem vai atender?"}
                        </p>
                    </div>
                    <button
                        data-testid="walkin-close"
                        onClick={handleClose}
                        className="h-9 w-9 rounded-lg border border-white/10 hover:border-white/30 text-slate-400 hover:text-white transition-all"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                {step === "search" && (
                    <div className="space-y-3">
                        <div className="relative">
                            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                            <input
                                data-testid="walkin-search"
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Buscar por nome, telefone ou e-mail..."
                                className="w-full min-h-[44px] rounded-xl bg-slate-900 border border-white/10 pl-9 pr-3 text-sm text-white outline-none focus:border-white/30"
                            />
                        </div>
                        <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                            {filteredClients.length === 0 && (
                                <p className="text-center text-slate-500 text-xs py-6">Nenhum cliente encontrado.</p>
                            )}
                            {filteredClients.map((c) => (
                                <button
                                    key={c.id}
                                    data-testid={`walkin-client-${c.id}`}
                                    onClick={() => handlePickExisting(c)}
                                    className="w-full min-h-[56px] flex items-center gap-3 rounded-xl border border-white/5 hover:border-white/20 bg-slate-900/50 hover:bg-slate-900 p-3 text-left transition-all"
                                >
                                    <div className="h-9 w-9 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center text-white text-sm font-bold">
                                        {c.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-white truncate">{c.name}</div>
                                        <div className="text-[11px] text-slate-400 font-mono truncate">{c.phone || "—"}</div>
                                    </div>
                                    <i className="fa-solid fa-arrow-right text-slate-500 text-xs"></i>
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="walkin-add-new"
                            onClick={() => {
                                setDraft({ name: query.replace(/\d/g, "").trim() || "", phone: query.replace(/\D/g, "") || "", email: "" });
                                setStep("create");
                            }}
                            className="w-full min-h-[44px] rounded-xl border border-dashed border-white/15 hover:border-white/40 text-slate-300 hover:text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
                        >
                            <i className="fa-solid fa-user-plus"></i>
                            Adicionar Novo Cliente
                        </button>
                    </div>
                )}

                {step === "create" && (
                    <div className="space-y-3">
                        <input
                            data-testid="walkin-create-name"
                            autoFocus
                            value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            placeholder="Nome completo"
                            className="w-full min-h-[44px] rounded-xl bg-slate-900 border border-white/10 px-3.5 text-sm text-white outline-none focus:border-white/30"
                        />
                        <input
                            data-testid="walkin-create-phone"
                            value={draft.phone}
                            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                            placeholder="Telefone (com DDD)"
                            className="w-full min-h-[44px] rounded-xl bg-slate-900 border border-white/10 px-3.5 text-sm text-white outline-none focus:border-white/30 font-mono"
                        />
                        <input
                            data-testid="walkin-create-email"
                            value={draft.email}
                            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                            placeholder="E-mail (opcional)"
                            className="w-full min-h-[44px] rounded-xl bg-slate-900 border border-white/10 px-3.5 text-sm text-white outline-none focus:border-white/30"
                        />
                        <div className="flex items-center justify-between gap-2 pt-2">
                            <button
                                onClick={() => setStep("search")}
                                className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 text-[11px] uppercase tracking-wider font-bold"
                            >
                                <i className="fa-solid fa-arrow-left mr-1"></i>Voltar
                            </button>
                            <button
                                data-testid="walkin-create-save"
                                onClick={handleQuickCreate}
                                className="min-h-[44px] px-5 rounded-xl bg-white text-slate-900 text-[11px] uppercase tracking-wider font-bold"
                            >
                                Salvar e avançar
                                <i className="fa-solid fa-arrow-right ml-2"></i>
                            </button>
                        </div>
                    </div>
                )}

                {step === "pro" && (
                    <div className="space-y-3">
                        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3 flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center text-white text-sm font-bold">
                                {(selectedClient?.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-white truncate">
                                    {selectedClient?.name || draft.name}
                                </div>
                                <div className="text-[11px] text-slate-400 truncate">
                                    {selectedClient?.phone || draft.phone || "Sem telefone"}
                                </div>
                            </div>
                            <span className="text-[9px] uppercase tracking-wider text-emerald-300 font-bold">
                                ✓ Cliente
                            </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto">
                            {professionals.filter((p) => p.active !== false).map((p) => {
                                const isSel = selectedProId === p.id;
                                return (
                                    <button
                                        key={p.id}
                                        data-testid={`walkin-pro-${p.id}`}
                                        onClick={() => setSelectedProId(p.id)}
                                        className={`min-h-[60px] flex items-center gap-3 rounded-xl border p-2.5 text-left transition-all ${
                                            isSel
                                                ? "border-emerald-400/60 bg-emerald-500/10"
                                                : "border-white/10 bg-slate-900/40 hover:border-white/30"
                                        }`}
                                    >
                                        <img src={p.avatar} alt="" className="h-9 w-9 rounded-lg object-cover" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs font-bold text-white truncate">{p.name}</div>
                                            <div className="text-[10px] text-slate-400 truncate">{p.role}</div>
                                        </div>
                                        {isSel && <i className="fa-solid fa-check text-emerald-300 text-xs"></i>}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex items-center justify-between gap-2 pt-2">
                            <button
                                onClick={() => { setStep("search"); setSelectedClient(null); }}
                                className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 text-[11px] uppercase tracking-wider font-bold"
                            >
                                <i className="fa-solid fa-arrow-left mr-1"></i>Trocar cliente
                            </button>
                            <button
                                data-testid="walkin-confirm"
                                onClick={handleConfirmPro}
                                disabled={!selectedProId}
                                className="min-h-[44px] px-5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[11px] uppercase tracking-wider font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                <i className="fa-solid fa-circle-play mr-2"></i>Abrir Comanda
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PosTerminal;
